import styled from '@emotion/styled'
import { useTheme } from '@mui/system'
import { useTranslation } from 'react-i18next'
import { RadioButtonChecked as RadioButtonCheckedIcon } from '@mui/icons-material'

import { PendingMatchStatus } from 'src/graphql/types'
import { useTimedOut } from 'src/screens/matches/collection/match/status/utils'
import { useStalled } from 'src/screens/matches/collection/match/status/use-progress'

export function Status(props: {
  current?: PendingMatchStatus
  className?: string
  courseProgress: number | null | undefined
  updatedAt: string
  createdAt: string
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const { current: currentStatus, updatedAt, createdAt, courseProgress } = props

  if (!currentStatus) throw new Error('Missing match status')

  const status = useComputedStatus(updatedAt, currentStatus, courseProgress)
  const isDone =
    status === PendingMatchStatus.Completed ||
    status === PendingMatchStatus.Failed ||
    status === PendingMatchStatus.Retired
  const isProcessing =
    status === PendingMatchStatus.Created ||
    status === PendingMatchStatus.Verified
  const stalled = useStalled(createdAt)
  const text = t(`matches:status:${status}`)
  const baseColor = useColor(status)
  const color = stalled && isProcessing ? theme.d.colors.error : baseColor

  return (
    <Box className={props.className}>
      {isDone && <DoneIcon statusColor={color} />}
      <Text statusColor={color}>{text}</Text>
    </Box>
  )
}

export function useComputedStatus(
  updatedAt: string,
  status: PendingMatchStatus,
  courseProgress: number | null | undefined
) {
  const isTimedOut = useTimedOut(updatedAt)

  // A retired match's row is years old, so it would read as timed out.
  if (status === PendingMatchStatus.Retired) return status

  const uncompletedStatus = isTimedOut ? PendingMatchStatus.Failed : status

  const partialCourseProgressStatus = isTimedOut
    ? status
    : PendingMatchStatus.Verified

  if (status !== PendingMatchStatus.Completed) {
    return uncompletedStatus
  }

  if (courseProgress !== 1) return partialCourseProgressStatus

  return status
}

function useColor(status: PendingMatchStatus) {
  const theme = useTheme()

  const color = {
    [PendingMatchStatus.Completed]: theme.d.colors.pendingMatchStatus.completed,
    [PendingMatchStatus.Created]: theme.d.colors.pendingMatchStatus.created,
    [PendingMatchStatus.Failed]: theme.d.colors.pendingMatchStatus.failed,
    [PendingMatchStatus.Verified]: theme.d.colors.pendingMatchStatus.verified,
    [PendingMatchStatus.Retired]: theme.d.colors.pendingMatchStatus.retired
  }
  return color[status]
}

const Box = styled.div`
  display: flex;
  align-items: center;
`

const DoneIcon = styled(
  ({
    statusColor: _,
    ...otherProps
  }: {
    statusColor: string
    className?: string
  }) => <RadioButtonCheckedIcon {...otherProps} />
)`
  color: ${(props) => props.statusColor};
  margin-right: 6px;
  width: 14px !important;
  height: 14px !important;
`

const Text = styled(
  ({
    statusColor: _,
    ...otherProps
  }: {
    statusColor: string
    children: string
  }) => <span {...otherProps} />
)`
  color: ${(props) => props.statusColor};
  font-weight: bold;
  line-height: 100%;
`
